import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

/**
 * Interactive before/after comparison slider.
 * Drag the handle (or touch/swipe) to reveal the old site underneath the new one.
 */
export const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel = 'PRIMA',
  afterLabel = 'DOPO',
  alt = '',
}) => {
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const updatePosition = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPosition(pct);
  }, []);

  const onPointerDown = (e) => {
    setIsDragging(true);
    updatePosition(e.clientX);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => updatePosition(e.clientX);
    const onUp = () => setIsDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isDragging, updatePosition]);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative w-full aspect-[16/10] md:aspect-video overflow-hidden rounded-lg bg-black select-none touch-none cursor-ew-resize"
      onPointerDown={onPointerDown}
    >
      {/* BEFORE (bottom layer - full image) */}
      <img
        src={beforeSrc}
        alt={`${alt} - ${beforeLabel}`}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />
      <div className="absolute top-4 left-4 z-20 bg-black/70 backdrop-blur-md border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase text-white/80 font-medium pointer-events-none">
        {beforeLabel}
      </div>

      {/* AFTER (top layer - clipped) */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img
          src={afterSrc}
          alt={`${alt} - ${afterLabel}`}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          draggable={false}
        />
        <div className="absolute top-4 right-4 z-20 bg-[#FFD700] text-black px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-semibold pointer-events-none">
          {afterLabel}
        </div>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-[#FFD700] z-30 pointer-events-none shadow-[0_0_20px_rgba(255,215,0,0.6)]"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#FFD700] flex items-center justify-center shadow-[0_0_30px_rgba(255,215,0,0.5)]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
            <polyline points="9 18 15 12 9 6" transform="translate(6 0)" />
          </svg>
        </div>
      </div>
    </div>
  );
};

/**
 * Tabbed before/after showcase. Receives an array of { key, label, before, after }.
 */
export const BeforeAfterGallery = ({ items, beforeLabel, afterLabel, title, subtitle, description }) => {
  const [activeKey, setActiveKey] = useState(items[0]?.key);
  const active = items.find((i) => i.key === activeKey) || items[0];

  return (
    <section className="py-20 relative z-10" data-testid="before-after-gallery">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-10">
            <h2 className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-4">{subtitle}</h2>
            <h3 className="font-display text-4xl md:text-5xl text-white leading-tight mb-4">{title}</h3>
            {description && (
              <p className="text-white/60 text-lg max-w-2xl font-light leading-relaxed">{description}</p>
            )}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8" role="tablist">
            {items.map((item) => {
              const isActive = item.key === active.key;
              return (
                <button
                  key={item.key}
                  role="tab"
                  data-testid={`before-after-tab-${item.key}`}
                  aria-selected={isActive}
                  onClick={() => setActiveKey(item.key)}
                  className={`px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase transition-all duration-300 border ${
                    isActive
                      ? 'bg-[#FFD700] text-black border-[#FFD700]'
                      : 'bg-transparent text-white/60 border-white/15 hover:text-white hover:border-white/40'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Slider */}
          <motion.div
            key={active.key}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <BeforeAfterSlider
              beforeSrc={active.before}
              afterSrc={active.after}
              beforeLabel={beforeLabel}
              afterLabel={afterLabel}
              alt={active.label}
            />
          </motion.div>

          <p className="text-white/30 text-xs tracking-[0.2em] uppercase mt-4 text-center">
            ← {beforeLabel} · {afterLabel} →
          </p>
        </motion.div>
      </div>
    </section>
  );
};
